/**
 * Shared type definitions for Migration resources
 * These types align with backend C# models from EDCi.ResourceMigrator.Common.Models
 * and EDCi.ResourceMigrator.API.Models namespaces
 */

/**
 * Migration status enum - matches MigrationStatus.cs in backend
 */
export enum MigrationStatus {
  Configuring = 0,
  ExportQueued = 1,
  ExportInProgress = 2,
  ExportComplete = 3,
  TransformationQueued = 4,
  TransformationInProgress = 5,
  TransformationComplete = 6,
  ImportQueued = 7,
  ImportInProgress = 8,
  ValidationFailed = 9,
  PartiallyCompleted = 10,
  Complete = 11,
  SnapshotComplete = 12,
  Failed = 13,
  // Rollback pipeline stages — sequential from Failed (13), matching MigrationStatus.cs
  RollbackQueued = 14,
  RollbackSetupInProgress = 15,
  RollbackPlanAwaitingApproval = 16,
  RollbackInProgress = 17,
  RollbackComplete = 18,
  RollbackPartiallyCompleted = 19,
  RollbackFailed = 20
}

/**
 * Advanced filtering configuration for a specific resource type during export
 * Matches ResourceFilter.cs in backend
 */
export interface ResourceFilter {
  /** Resource type this filter applies to (e.g., "group", "routing_queue") */
  resourceType: string;
  /** Name patterns to INCLUDE (wildcards supported: * for any characters) */
  includePatterns?: string[];
  /** Name patterns to EXCLUDE (wildcards supported) - takes precedence over includePatterns */
  excludePatterns?: string[];
  /** Attribute-based filters in format "attribute:value" or "attribute:pattern*" */
  attributeFilters?: string[];
  /** Logical condition for combining filters: "AND" or "OR" (default: "AND") */
  condition?: string;
}

/**
 * Transformation definition - matches TransformationDefinition.cs
 */
export interface TransformationDefinition {
  /** Whether this transformation is enabled */
  enabled: boolean;
  /** Name/type of the transformation (must match a registered transformation) */
  name: string;
  /** Execution order (lower numbers execute first) */
  order?: number;
  /** Transformation-specific parameters */
  parameters?: Record<string, unknown>;
  /** List of resource types this transformation applies to (empty = all types) */
  applyToTypes?: string[];
  /** Description of what this transformation does */
  description?: string;
}

/**
 * Global settings for the transformation process - matches TransformationSettings.cs
 */
export interface TransformationSettings {
  /** Log level (e.g., "Debug", "Information", "Warning", "Error") */
  logLevel?: string;
  /** Whether to validate files before applying transformations */
  validateBeforeApply?: boolean;
  /** Whether to create backup files before transformation (defaults to false) */
  createBackups?: boolean;
  /** Whether to stop on first error or continue with remaining files */
  stopOnError?: boolean;
  /** Maximum number of parallel file operations */
  maxParallelOperations?: number;
}

/**
 * Complete transformation configuration - matches TransformationConfig.cs
 */
export interface TransformationConfig {
  /** List of transformations to execute */
  transformations: TransformationDefinition[];
  /** Global settings that apply to all transformations */
  settings?: TransformationSettings;
}

/**
 * Statistics for a single transformation execution
 */
export interface TransformationStatistics {
  /** Name of the transformation (e.g., "DeleteInvalidData", "DivisionMapping") */
  transformationName: string;
  /** Number of changes made by this transformation */
  changesCount: number;
  /** List of files affected by this transformation */
  affectedFiles: string[];
  /** Order in which this transformation was applied (from config) */
  order: number;
}

/**
 * Single example of a transformation
 */
export interface TransformationExample {
  /** Type of transformation applied */
  transformationType: string;
  /** Resource type being transformed */
  resourceType: string;
  /** Original value before transformation */
  originalValue: string;
  /** Transformed value after transformation */
  transformedValue: string;
  /** File where the transformation occurred */
  filePath: string;
}

/**
 * Summary of changes made during transformation - matches TransformationDiff.cs
 */
export interface TransformationDiff {
  /** Number of resource names changed during transformation */
  namesChanged: number;
  /** Number of resource references updated during transformation */
  referencesUpdated: number;
  /** Number of division assignments applied during transformation */
  divisionAssignmentsApplied: number;
  /** Number of files modified during transformation */
  filesModified: number;
  /** Number of files that passed through without changes */
  filesUnchanged: number;
  /** Number of invalid YAML entries removed during cleaning (across all files) */
  yamlEntriesRemoved: number;
  /** List of YAML files that had invalid entries removed during cleaning */
  yamlCleanedFiles: string[];
  /** List of files that were modified */
  modifiedFiles: string[];
  /** List of files that passed through unchanged */
  unchangedFiles: string[];
  /** Example transformations showing before/after samples (limited to first 10) */
  examples: TransformationExample[];
  /** Per-transformation statistics showing changes and affected files for each transformation */
  transformationStats: TransformationStatistics[];
}

/**
 * Breakdown of exported files by type
 */
export interface FileTypeBreakdown {
  terraformFiles: number;
  terraformJsonFiles: number;
  jsonFiles: number;
  stateFiles: number;
  otherFiles: number;
}

/**
 * Summary statistics for a single resource type
 */
export interface ResourceTypeSummary {
  resourceType: string;
  fileCount: number;
  bytes: number;
}

/**
 * Classification of import errors to help determine retry strategy
 */
export enum ImportErrorType {
  /** No error - import succeeded */
  None = 0,
  /** Transient error that may succeed on retry */
  Transient = 1,
  /** Permanent error that won't succeed on retry */
  Permanent = 2,
  /** Validation error (failed pre-import validation checks) */
  ValidationError = 3,
  /** Quota/limit exceeded in target org */
  QuotaExceeded = 4,
  /** Dependency missing (resource depends on another resource that doesn't exist) */
  DependencyMissing = 5,
  /** Resource already exists in target org (conflict) */
  AlreadyExists = 6,
  /** API rate limit exceeded (429 Too Many Requests) */
  RateLimitExceeded = 7,
  /** Unknown error */
  Unknown = 99
}

/**
 * Simple status enum for import results
 */
export enum ImportStatus {
  Pending,
  InProgress,
  Success,
  FailedRetryable,
  FailedPermanent,
  Skipped
}

/**
 * Historical snapshot entry for recurring snapshot schedules
 * Represents a single snapshot capture in the history
 */
export interface SnapshotHistoryEntry {
  /** When this snapshot was captured */
  capturedAtUtc: string;
  /** Path to blob storage for this snapshot */
  blobPath: string;
  /** Status of the snapshot (e.g., "Complete", "Failed") */
  status: string;
  /** Number of files in this snapshot */
  fileCount?: number;
  /** Total size of snapshot in bytes */
  totalBytes?: number;
}

/**
 * Result of importing a single resource during Job3Import
 */
export interface ImportResourceResult {
  /** Whether the resource was imported successfully */
  success: boolean;
  /** Type of resource (e.g., "genesyscloud_routing_queue") */
  resourceType: string;
  /** Name of the resource being imported */
  resourceName: string;
  /** Full Terraform address (e.g., "genesyscloud_routing_queue.example_queue") */
  resourceAddress: string;
  /** Genesys Cloud resource ID in target org (populated on success) */
  targetResourceId?: string;
  /** Error message if import failed */
  errorMessage?: string;
  /** Category of error */
  errorType: ImportErrorType;
  /** When the import was attempted */
  attemptedAt: string;
  /** Number of retry attempts made (0 = first attempt) */
  retryCount: number;
  /** Duration of the import operation in milliseconds */
  durationMs: number;
}

/**
 * Job summary for a migration stage (Export, Transform, or Import)
 * Matches MigrationJobSummary.cs in backend
 */
export interface MigrationJobSummary {
  /** Job status string */
  status?: string;
  /** Number of artifacts/resources processed */
  artifactCount?: number;
  /** Path to blob storage for this job's artifacts */
  blobPath?: string;
  /** Additional notes about the job */
  notes?: string;
  /** When the job started */
  startedAtUtc?: string;
  /** When the job completed */
  completedAtUtc?: string;
  /** Total size of exported files in bytes */
  totalBytes?: number;
  /** Breakdown of files by type */
  fileBreakdown?: FileTypeBreakdown;
  /** Number of resource types that were configured for export */
  configuredResourceTypes?: number;
  /** Number of resource types that had data exported */
  resourceTypesWithData?: number;
  /** Per-resource-type statistics (top 10 by size) */
  resourceStats?: ResourceTypeSummary[];
  /** Errors encountered during processing */
  errors?: string[];
  /** Warnings generated during processing */
  warnings?: string[];
  /** Whether the job completed successfully */
  success?: boolean;
  /** Summary of changes made during transformation (Job2Transform only) */
  changesSummary?: TransformationDiff;
  /** If true, import continues processing remaining resources even if individual resources fail */
  continueOnError?: boolean;
  /** If true, runs import in dry-run mode (preview) without actually creating resources */
  dryRun?: boolean;
  /** Per-resource import results when ContinueOnError is enabled */
  resourceResults?: ImportResourceResult[];
}

/**
 * Complete migration record - matches Migration.cs in backend
 */
export interface Migration {
  /** Unique identifier for this migration */
  id: string;
  /** Correlation ID for tracing this migration request across all services and logs */
  correlationId?: string;
  /** Display name for the migration */
  name: string;
  /** Company/Customer ID this migration belongs to */
  customerId: string;
  /** Email of user who created the migration */
  createdBy?: string;
  /** Source Genesys Cloud organization ID */
  sourceOrgId: string;
  /** Source Genesys Cloud region (e.g., us-east-1) */
  sourceRegion?: string;
  /** Target Genesys Cloud organization ID */
  targetOrgId: string;
  /** Target Genesys Cloud region (e.g., us-east-1) */
  targetRegion?: string;
  /** Source Genesys Cloud organization Name */
  sourceOrgName: string;
  /** Target Genesys Cloud organization Name */
  targetOrgName: string;
  /** Types of resources to migrate (e.g., "queues", "users", "skills") */
  resources: string[];
  /** Simple wildcard patterns for resources to EXCLUDE from export */
  excludePatterns?: string[];
  /** Advanced filtering rules per resource type */
  filters?: ResourceFilter[];
  /** If true, exports ALL supported Genesys Cloud resource types */
  exportAll: boolean;
  /** If true, automatically discovers and includes dependencies for explicitly requested resources */
  includeDependencies: boolean;
  /** True if this is a snapshot (backup/export-only), false if full migration */
  isSnapshot: boolean;
  /** Indicates if this snapshot/migration is active and should be scheduled */
  isActive: boolean;
  /** Cron expression for when the snapshot should run */
  snapshotCronExpression?: string;
  /** Current migration status/stage */
  status: MigrationStatus | number | string; // Can be enum, numeric, or string from backend
  /** When the migration was created/started */
  createdAtUtc: string;
  /** When the migration was last updated */
  updatedAtUtc?: string;
  /** Link to current snapshot blob (current state of migration data) */
  snapshotBlobUrl?: string;
  /** Historical snapshots for recurring schedules */
  snapshotHistory?: SnapshotHistoryEntry[];
  /** Maximum number of historical snapshots to retain (default: 30) */
  snapshotRetentionLimit?: number;
  /** Link to results blob (completed migration artifacts/results) */
  resultsBlobUrl?: string;
  /** Path to customer-specific files (templates, configs) */
  customerFilesPath?: string;
  /** Transformation configuration to apply (single object, not array) */
  transformationConfig?: TransformationConfig;
  /** Optional: Use existing staging directory instead of creating new one */
  existingStagingPath?: string;
  /** Export job summary */
  job1Export?: MigrationJobSummary;
  /** Transform job summary */
  job2Transform?: MigrationJobSummary;
  /** Import job summary */
  job3Import?: MigrationJobSummary;
  /** Rollback setup job summary (job4 — generates rollback plan, awaits approval) */
  job4RollbackSetup?: MigrationJobSummary;
  /** Rollback apply job summary (job5 — executes approved rollback plan) */
  job5RollbackApply?: MigrationJobSummary;
  /**
   * TODO: REMOVE AFTER MOCKUP — demo-only field. When set, marks this row as
   * a synthetic "rollback in progress" row that references the original
   * migration being rolled back. Used to render the inline status sub-label
   * on the parent row. Delete when the real rollback feature is wired up.
   */
  parentMigrationId?: string;
}

/**
 * Request model for creating a new migration
 * Matches CreateMigrationRequest.cs in backend
 */
export interface CreateMigrationRequest {
  /** Display name for the migration */
  name: string;
  /** Company/Customer ID this migration belongs to */
  customerId: string;
  /** Email of user who created the migration */
  createdBy?: string;
  /** Source Genesys Cloud organization ID */
  sourceOrgId: string;
  /** Source Genesys Cloud organization Name */
  sourceOrgName: string;
  /** Target Genesys Cloud organization ID (optional for snapshots) */
  targetOrgId?: string;
  /** Target Genesys Cloud organization Name (optional for snapshots) */
  targetOrgName?: string;
  /** Which resources should be migrated - by resource name and type */
  resources: string[];
  /** Simple wildcard patterns for resources to EXCLUDE from export */
  excludePatterns?: string[];
  /** Advanced filtering rules per resource type */
  filters?: ResourceFilter[];
  /** If true, exports ALL supported Genesys Cloud resource types */
  exportAll?: boolean;
  /** Path to customer-specific files (templates, configs) */
  customerFilesPath?: string;
  /** Transformation configuration to apply */
  transformationConfig?: TransformationConfig;
  /** Optional: Use existing staging directory instead of creating new one */
  existingStagingPath?: string;
  /** The cron expression for when the snapshot should run */
  snapshotCronExpression?: string;
  /** Should dependencies be included in the export */
  includeDependencies?: boolean;
}

/**
 * Request model for updating an existing migration
 * Matches UpdateMigrationRequest.cs in backend
 */
export interface UpdateMigrationRequest {
  /** Display name for the migration */
  name?: string;
  /** Source Genesys Cloud organization ID */
  sourceOrgId?: string;
  /** Source Genesys Cloud organization Name */
  sourceOrgName?: string;
  /** Target Genesys Cloud organization ID */
  targetOrgId?: string;
  /** Target Genesys Cloud organization Name */
  targetOrgName?: string;
  /** Which resources should be migrated - by resource name and type */
  resources?: string[];
  /** Simple wildcard patterns for resources to EXCLUDE from export */
  excludePatterns?: string[];
  /** Advanced filtering rules per resource type */
  filters?: ResourceFilter[];
  /** If true, exports ALL supported Genesys Cloud resource types */
  exportAll?: boolean;
  /** Update the migration status to next stage or specific status */
  status?: MigrationStatus;
  /** Path to customer-specific files (templates, configs) */
  customerFilesPath?: string;
  /** Transformation configuration to apply */
  transformationConfig?: TransformationConfig;
  /** Optional: Use existing staging directory instead of creating new one */
  existingStagingPath?: string;
  /** Update snapshot blob URL */
  snapshotBlobUrl?: string;
  /** Update results blob URL */
  resultsBlobUrl?: string;
  /** If true, automatically advance to the next status after updating */
  advanceToNextStatus?: boolean;
}

/**
 * Response when creating a migration
 */
export interface CreateMigrationResponse {
  id: string;
  name: string;
  status: string;
  createdAtUtc: string;
}

/**
 * Response when updating a migration
 */
export interface UpdateMigrationResponse {
  id: string;
  name: string;
  customerId: string;
  sourceOrgId: string;
  sourceOrgName: string;
  targetOrgId: string;
  targetOrgName: string;
  resources: string[];
  status: MigrationStatus;
  createdAtUtc: string;
  updatedAtUtc: string;
  snapshotBlobUrl?: string;
  resultsBlobUrl?: string;
  transformationConfig?: TransformationConfig;
}

export interface SkipTransformationResponse {
  id: string;
  message: string;
  status: string;
  filesCopied: number;
  transformJob: {
    status: string;
    artifactCount: number;
    blobPath: string;
  };
}
